<template>
  <div id="app">
    <confirm-modal ref="appConfirmationModal"></confirm-modal>

    <b-navbar toggleable="md" type="dark" variant="dark">
      <b-navbar-toggle target="nav_collapse"></b-navbar-toggle>
      <b-navbar-brand to="/">HEUREKA!</b-navbar-brand>

      <b-collapse is-nav id="nav_collapse">
        <navbar-teacher v-if="isAuthorized()"></navbar-teacher>
        <navbar-guest v-else></navbar-guest>

        <navbar-admin v-if="isAdmin()"></navbar-admin>

        <b-navbar-nav v-if="isAuthorized()"  class="ml-auto">
          <b-nav-item  @click="$eventHub.$emit('logout')">Logout</b-nav-item>
          <gravatar :user=this.getUser() :props=gravatar.props></gravatar>
        </b-navbar-nav>

        <b-navbar-nav v-else class="ml-auto">
          <b-nav-item-dropdown :text="currentCountry.name" type="dark" right>
            <b-dropdown-item v-for="(country, tld) in allCountries" :key="tld" :href="'http://' + country.url">
              {{ country.name }}
            </b-dropdown-item>
          </b-nav-item-dropdown>
        </b-navbar-nav>

      </b-collapse>

    </b-navbar>
    <b-container id="main" ref="main">
      <div id="defaultAlert" ref="defaultAlert">
        <b-alert v-bind:show="error.show" v-bind:variant="error.variant" dismissible fade>
          <span v-html="error.message"></span>
        </b-alert>
      </div>

      <!-- content will be rendered here -->
      <router-view />
      <cookie-law message="Diese Webseite verwendet lediglich notwendige Cookies" buttonText="Einverstanden" buttonClass="btn btn-primary"></cookie-law>
    </b-container>
  </div>
</template>

<script>
import NavbarGuest from '@/components/navbar/Guests'
import NavbarTeacher from '@/components/navbar/Users'
import NavbarAdmin from '@/components/navbar/Admin'
import Gravatar from '@/components/user/Gravatar'
import ConfirmModal from '@/components/misc/ConfirmModal'
import CookieLaw from 'vue-cookie-law'
import * as Sentry from '@sentry/browser'
import {getCountries, getCountry, getCountryByHost} from './lib/locale'

const HttpStatus = {
  Okay: 200,
  BadRequest: 400,
  Unauthorized: 401,
  Forbidden: 403,
  NotFound: 404,
  PreconditionFailed: 412,
  InternalServerError: 500
}
export default {
  name: 'app',

  components: {
    'navbar-guest': NavbarGuest,
    'navbar-teacher': NavbarTeacher,
    'navbar-admin': NavbarAdmin,
    'confirm-modal': ConfirmModal,
    CookieLaw,
    Gravatar
  },

  data: function () {
    return {
      gravatar: {
        props: {
          width: 40,
          height: 40
        }
      },
      error: {
        show: false,
        variant: 'danger',
        message: ''
      }
    }
  },

  computed: {
    currentCountry: function () {
      return getCountryByHost()
    },
    allCountries: function () {
      return getCountries()
    }
  },

  created: function () {
    this.checkSession()

    this.$eventHub.$on('auth', this.handleAuthResponse)
    this.$eventHub.$on('login', this.login)
    this.$eventHub.$on('logout', this.logout)
    this.$eventHub.$on('confirm', this.confirm)
    this.$eventHub.$on('download', this.getAttachment)
    this.$eventHub.$on(['info', 'success', 'alertInfo'], this.alertInfo)
    this.$eventHub.$on(['error', 'alertError'], this.alertError)
    this.$eventHub.$on(['hide', 'alertHide'], this.alertHide)
  },

  beforeDestroy: function () {
    this.$eventHub.$off([
      'auth', 'login', 'logout',
      'info', 'alertInfo', 'error', 'alertError', 'hide', 'alertHide',
      'download', 'confirm'
    ])
  },

  watch: {
    '$route': function (route) {
      if (route.params && route.params.alert) {
        let alert = route.params.alert
        this.$eventHub.$emit(alert.variant, alert.message)
      }
    }
  },

  methods: {
    alertError: function (error, target = null, hideIn = true) {
      this.alertShow('danger', error, target, hideIn)
    },

    alertInfo: function (msg, target = null, hideIn = true) {
      this.alertShow('info', msg, target, hideIn)
    },

    alertShow: function (variant, msg, target = null, hideIn = true) {
      this.error.variant = variant
      this.error.message = this.getErrorMessage(msg)
      this.error.show = hideIn > 0 ? hideIn : true

      try {
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'})
      } catch (e) {
        // fallback to legacy signature w/o smooth scroll behaviour
        window.scrollTo(0, 0)
      }
    },

    alertHide: function () {
      this.error.show = false
    },

    confirm (message, title, confirmCallback) {
      this.$refs.appConfirmationModal.show(message, title, confirmCallback)
    },

    getErrorMessage: function (error) {
      if (typeof error === 'string') {
        return error
      }

      // default error message
      let message = 'Ein unbekannter Fehler ist aufgetreten'

      // try to extract error from response
      if (error.response && error.response.data && error.response.data.error) {
        let errorData = error.response.data.error
        if (errorData.translations && errorData.translations['de']) {
          message = errorData.translations['de']
        }
      }

      return message
    },

    isAuthorized: function () {
      return localStorage.getItem('auth') !== null
    },

    checkSession: function () {
      if (!this.$route.meta || !this.$route.meta.authRequired) {
        return
      }

      this.axios.get('/auth')
        .then(rsp => {
          if (!rsp.data.active && this.$route.name !== 'login') {
            this.$router.push({name: 'login', params: {from: 'mounted'}})
          }
        })
    },

    isAdmin: function () {
      let user = this.getUser()

      if (user === null) {
        return false
      }

      if (user.role.id === 0) {
        return true
      }
      return false
    },

    isUser: function () {
      return !this.isAdmin()
    },

    getUser: function () {
      let json = localStorage.getItem('user')

      if (json === null) {
        return null
      }

      return JSON.parse(json)
    },

    getSchool: function () {
      let user = this.getUser()

      if (user === null) {
        return null
      }

      if (!user.hasOwnProperty('school')) {
        return null
      }

      return user.school
    },

    login: function (username, password) {
      let body = {
        username: username,
        password: password,
        client_id: this.$apiConfig.client_id,
        redirect_url: this.$apiConfig.redirect_url
      }

      this.axios.post('/auth', JSON.stringify(body))

        .then((rsp) => {
          this.handleAuthResponse(rsp)
        })

        .catch((err) => {
          // Email not verified
          if (err.response && err.response.status && err.response.status === HttpStatus.PreconditionFailed) {
            let msg = 'Ihre Emailadresse ist noch nicht bestätigt'
            this.alertError(msg)

            return
          }

          this.alertError(err)
        })
    },

    handleAuthResponse: function (rsp, from = null, to = null) {
      const user = rsp.data.user

      // country code of user does not match current host
      if (user.country !== getCountryByHost().code) {
        const country = getCountry(user.country)

        let msg = 'Ihr Account ist in ' + country.name + ' registriert. '
        msg += 'Klicken Sie <a href="http://' + country.url + '">hier</a> um auf die richtige Seite geleitet zu werden.'

        this.alertError(msg)

        return
      }

      const auth = rsp.data

      localStorage.setItem('auth', JSON.stringify(auth))
      localStorage.setItem('user', JSON.stringify(user))

      Sentry.setUser({
        id: user.id,
        email: user.email
      })

      if (!to) {
        to = 'teacher.dashboard'
      }

      let route = {
        name: to,
        params: {from: from}
      }

      if (this.$router.currentRoute.name !== to) {
        this.$router.push(route).catch(() => {})
      }
    },

    logout: function () {
      this.removeAuth()

      if (this.$route.name !== 'login') {
        this.$router.push({name: 'login', params: {from: 'logout'}})
      }
    },

    removeAuth: function () {
      localStorage.removeItem('user')
      localStorage.removeItem('auth')
    },

    getAttachment: function (url, method, data, resolve, reject) {
      if (!method) {
        method = 'GET'
      }

      this.axios({
        url,
        method,
        data,
        responseType: 'blob'
      })
        .then(function (response) {
          let filename = 'export.csv'
          if (response.headers['content-disposition']) {
            let cdp = response.headers['content-disposition']
            filename = cdp.split('=')[1]
          }

          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()

          if (typeof resolve === 'function') {
            resolve()
          }
        })
        .catch((err) => {
          if (typeof reject === 'function') {
            reject(err)
          }
        })
    }
  }
}
</script>

<style>
body {
  margin: 0;
}

#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

#main {
  margin-top: 40px;
}

header {
  margin: 0;
  height: 56px;
  padding: 0 16px 0 24px;
  background-color: #35495E;
  color: #ffffff;
}

header span {
  display: block;
  position: relative;
  font-size: 20px;
  line-height: 1;
  letter-spacing: .02em;
  font-weight: 400;
  box-sizing: border-box;
  padding-top: 16px;
}

.text-line-through {
  text-decoration: line-through;
}

.clickable {
  cursor: pointer;
}

.red {
  color: red;
}
</style>



// WEBPACK FOOTER //
// src/App.vue